import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { getFeatureModules, getFeatures } from '../api/entitlementsApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { Tab, Tabs } from '../components/ui/Tabs';

const { semantic } = colorTokens;

const TAB_CAPABILITIES = 'capabilities';
const TAB_PERMISSIONS = 'permissions';
const TAB_LEGACY = 'legacy';

const CATEGORY_FILTERS = [
  { value: '', labelKey: 'features:filters.all' },
  { value: 'core', labelKey: 'features:corePermissions' },
  { value: 'paid_module', labelKey: 'features:paidModulePermissions' },
  { value: 'future_override', labelKey: 'features:futureOverridePermissions' },
  { value: 'support_dual_flow', labelKey: 'features:filters.supportGap' },
];

const SOURCE_TYPE_FILTERS = [
  { value: '', labelKey: 'features:filters.all' },
  { value: 'menu_page', labelKey: 'features:filters.menuPage' },
  { value: 'registry_page', labelKey: 'features:filters.registryPage' },
  { value: 'support_flow', labelKey: 'features:filters.supportFlow' },
  { value: 'scheduled_job', labelKey: 'features:filters.scheduledJob' },
];

const ACCESS_CATEGORY_TONE = {
  core: 'Brand',
  paid_module: 'Success',
  future_override: 'Warning',
  support_dual_flow: 'Info',
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const tableSx = {
  overflowX: 'auto',
  width: '100%',
  '& table': {
    width: '100%',
    minWidth: '100%',
  },
};

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

function PermissionCell({ value, emptyValue }) {
  if (!value) {
    return emptyValue;
  }

  return (
    <Typography component="span" title={String(value)} sx={{ ...monoSx, maxWidth: 180, display: 'inline-block' }}>
      {value}
    </Typography>
  );
}

function ListCell({ items, emptyValue }) {
  if (!items?.length) {
    return emptyValue;
  }

  return (
    <Typography sx={{ ...monoSx, maxWidth: 220, whiteSpace: 'normal' }}>
      {items.join(', ')}
    </Typography>
  );
}

function CategoryBadge({ category, emptyValue }) {
  if (!category) {
    return emptyValue;
  }

  return (
    <StatusBadge
      status={category}
      category="generic"
      apiLabel={category}
      size="Sm"
      tone={ACCESS_CATEGORY_TONE[category] || 'Neutral'}
    />
  );
}

export function FeaturesPage() {
  const { t } = useTranslation(['features', 'navigation', 'common']);
  const [activeTab, setActiveTab] = useState(TAB_CAPABILITIES);
  const [features, setFeatures] = useState([]);
  const [modules, setModules] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [guardPermissionFilter, setGuardPermissionFilter] = useState('');
  const [globalRequiredFilter, setGlobalRequiredFilter] = useState('');

  const emptyValue = t('common:empty_value');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};

      if (activeTab === TAB_LEGACY) {
        params.source = 'legacy';
        params.include_legacy = true;
        params.include_internal = true;
      } else if (activeTab === TAB_PERMISSIONS) {
        params.source = 'permissions';
        if (moduleFilter) {
          params.module_code = moduleFilter;
        }
        if (categoryFilter) {
          params.access_category = categoryFilter;
          if (categoryFilter === 'support_dual_flow') {
            params.include_support_gap = true;
          }
        }
      } else {
        params.source = 'functional_capabilities';
        if (moduleFilter) {
          params.module_code = moduleFilter;
        }
        if (categoryFilter) {
          params.access_category = categoryFilter;
        }
        if (sourceTypeFilter) {
          params.source_type = sourceTypeFilter;
        }
        if (guardPermissionFilter) {
          params.guard_permission = guardPermissionFilter;
        }
        if (globalRequiredFilter !== '') {
          params.is_global_required = globalRequiredFilter === 'true';
        }
      }

      const [featuresResponse, modulesResponse] = await Promise.all([
        getFeatures(params),
        getFeatureModules({ source: 'mafertapp' }),
      ]);

      setFeatures(featuresResponse.data || []);
      setMeta(featuresResponse.meta || null);
      setModules(modulesResponse.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    moduleFilter,
    categoryFilter,
    sourceTypeFilter,
    guardPermissionFilter,
    globalRequiredFilter,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const guardPermissionOptions = useMemo(() => {
    const values = new Set();
    modules.forEach((module) => {
      if (module.permission_rows_count > 0) {
        values.add('access settings');
        values.add('access users');
        values.add('access company settings');
        values.add('access maintenance');
      }
    });

    return [
      'access settings',
      'access users',
      'access company settings',
      'access maintenance',
      'access training',
      'access service_task',
    ].filter((value) => values.has(value) || activeTab === TAB_CAPABILITIES);
  }, [modules, activeTab]);

  const capabilityColumns = useMemo(() => [
    {
      id: 'module_customer_name',
      label: t('common:module'),
      render: (row) => row.module_customer_name || row.module_code || emptyValue,
    },
    {
      id: 'title',
      label: t('features:capability'),
      render: (row) => (
        <Stack spacing={0.5} sx={{ minWidth: 0, maxWidth: 260 }}>
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamily,
              fontSize: 13,
              lineHeight: '20px',
              color: semantic.text.primary,
            }}
          >
            {row.title || row.title_sk || emptyValue}
          </Typography>
          {row.capability_code && (
            <Typography sx={monoSx}>{row.capability_code}</Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'page_key',
      label: t('features:columns.pageKey'),
      render: (row) => (
        row.page_key
          ? <Typography sx={monoSx}>{row.page_key}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'data_tables',
      label: t('features:columns.dataTables'),
      render: (row) => <ListCell items={row.data_tables} emptyValue={emptyValue} />,
    },
    {
      id: 'guard_permission',
      label: t('features:columns.guardPermission'),
      render: (row) => <PermissionCell value={row.guard_permission} emptyValue={emptyValue} />,
    },
    {
      id: 'source_type',
      label: t('features:columns.sourceType'),
      render: (row) => (
        row.source_type
          ? (
            <StatusBadge
              status={row.source_type}
              category="generic"
              apiLabel={row.source_type}
              size="Sm"
              tone="Neutral"
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'seed_scope',
      label: t('features:columns.seedScope'),
      render: (row) => (
        row.seed_scope
          ? <Typography sx={monoSx}>{row.seed_scope}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'access_category',
      label: t('common:category'),
      render: (row) => <CategoryBadge category={row.access_category} emptyValue={emptyValue} />,
    },
    {
      id: 'depends_on_global_settings',
      label: t('features:columns.globalDependency'),
      render: (row) => <YesNoBadge value={Boolean(row.depends_on_global_settings)} t={t} />,
    },
    {
      id: 'notes',
      label: t('features:columns.notes'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            lineHeight: '20px',
            color: semantic.text.secondary,
            maxWidth: 260,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {row.notes || emptyValue}
        </Typography>
      ),
    },
  ], [emptyValue, t]);

  const permissionColumns = useMemo(() => [
    {
      id: 'module_customer_name',
      label: t('common:module'),
      render: (row) => row.module_customer_name || row.module_code || emptyValue,
    },
    {
      id: 'label',
      label: t('features:columns.roleAddLabel'),
      render: (row) => (
        <Stack spacing={0.5} sx={{ minWidth: 0, maxWidth: 260 }}>
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamily,
              fontSize: 13,
              lineHeight: '20px',
              color: semantic.text.primary,
            }}
          >
            {row.label || row.label_key || emptyValue}
          </Typography>
          {row.label_key && (
            <Typography sx={monoSx}>{row.label_key}</Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'permission_view',
      label: t('features:columns.view'),
      render: (row) => <PermissionCell value={row.permission_view} emptyValue={emptyValue} />,
    },
    {
      id: 'permission_add',
      label: t('features:columns.add'),
      render: (row) => <PermissionCell value={row.permission_add} emptyValue={emptyValue} />,
    },
    {
      id: 'permission_edit',
      label: t('features:columns.edit'),
      render: (row) => <PermissionCell value={row.permission_edit} emptyValue={emptyValue} />,
    },
    {
      id: 'permission_delete',
      label: t('features:columns.delete'),
      render: (row) => <PermissionCell value={row.permission_delete} emptyValue={emptyValue} />,
    },
    {
      id: 'access_category',
      label: t('common:category'),
      render: (row) => <CategoryBadge category={row.access_category} emptyValue={emptyValue} />,
    },
    {
      id: 'requires_paid_module',
      label: t('features:columns.requiresPaidModule'),
      render: (row) => <YesNoBadge value={Boolean(row.requires_paid_module)} t={t} />,
    },
    {
      id: 'requires_override',
      label: t('features:columns.requiresOverride'),
      render: (row) => <YesNoBadge value={Boolean(row.requires_override)} t={t} />,
    },
    {
      id: 'is_gdpr_sensitive',
      label: t('features:columns.gdpr'),
      render: (row) => <YesNoBadge value={Boolean(row.is_gdpr_sensitive)} t={t} />,
    },
    {
      id: 'source',
      label: t('features:columns.source'),
      render: (row) => (
        row.source
          ? (
            <StatusBadge
              status={row.source}
              category="generic"
              apiLabel={row.source}
              size="Sm"
              tone="Neutral"
            />
          )
          : emptyValue
      ),
    },
  ], [emptyValue, t]);

  const legacyColumns = useMemo(() => [
    {
      id: 'code',
      label: t('features:features.columns.code'),
      render: (row) => (
        row.code
          ? <Typography sx={monoSx}>{row.code}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'name',
      label: t('common:name'),
      render: (row) => row.name || emptyValue,
    },
    {
      id: 'module',
      label: t('features:features.columns.module'),
      render: (row) => row.module?.name || row.module_id || emptyValue,
    },
  ], [emptyValue, t]);

  const columns = useMemo(() => {
    if (activeTab === TAB_LEGACY) {
      return legacyColumns;
    }
    if (activeTab === TAB_PERMISSIONS) {
      return permissionColumns;
    }
    return capabilityColumns;
  }, [activeTab, capabilityColumns, permissionColumns, legacyColumns]);

  const groupedFeatures = useMemo(() => {
    if (activeTab === TAB_LEGACY) {
      return [{ key: 'legacy', label: t('features:legacyInternalHidden'), rows: features }];
    }

    const groups = new Map();
    features.forEach((row) => {
      const key = row.module_code || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: row.module_customer_name || key,
          rows: [],
        });
      }
      groups.get(key).rows.push(row);
    });

    return Array.from(groups.values());
  }, [features, activeTab, t]);

  const handleTabChange = (_event, value) => {
    setActiveTab(value);
    setModuleFilter('');
    setCategoryFilter('');
    setSourceTypeFilter('');
    setGuardPermissionFilter('');
    setGlobalRequiredFilter('');
  };

  const supportGapRows = features.filter((row) => row.is_support_gap);
  const capabilitiesEmpty = activeTab === TAB_CAPABILITIES && (meta?.capabilities_count ?? features.length) === 0;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('features:technicalFeaturesTitle')}
        subtitle={t('features:functionalCapabilitySubtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/billing/module-prices">
              {t('features:openModulePricing')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/system-modules/mafertapp">
              {t('features:openMafertAppModules')}
            </Button>
          </Stack>
        )}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadData} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('features:features.load_failed')}
        </Alert>
      )}

      {!error && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('features:functionalCapabilityBanner')}
          </Alert>

          {capabilitiesEmpty && !loading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('features:capabilitiesNotSeeded')}
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  mb: 0,
                  fontSize: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                php artisan system:sync-mafertapp-functional-capabilities
              </Box>
            </Alert>
          )}

          {activeTab === TAB_PERMISSIONS && supportGapRows.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {supportGapRows[0].support_permission_gap_note || t('features:supportPermissionGap')}
            </Alert>
          )}

          {activeTab === TAB_PERMISSIONS && meta && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('features:permissions.summary', {
                rows: meta.permission_rows_count,
                strings: meta.permission_strings_count,
              })}
            </Alert>
          )}

          {activeTab === TAB_CAPABILITIES && meta && meta.capabilities_count > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('features:capabilities.summary', {
                count: meta.capabilities_count,
                modules: meta.modules_count,
              })}
            </Alert>
          )}

          {activeTab === TAB_LEGACY && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('features:noNotificationTemplatesHere')}
            </Alert>
          )}
        </>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} appearance="Primary" sx={{ mb: 2 }}>
        <Tab value={TAB_CAPABILITIES} label={t('features:tabs.capabilities')} />
        <Tab value={TAB_PERMISSIONS} label={t('features:tabs.permissions')} />
        <Tab value={TAB_LEGACY} label={t('features:tabs.legacy')} />
      </Tabs>

      {activeTab !== TAB_LEGACY && (
        <FilterBar type="Simple">
          <FilterSelect
            label={t('common:module')}
            hideLabel
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            minWidth={200}
          >
            <FilterSelectOption value="">{t('features:filters.all')}</FilterSelectOption>
            {modules.map((module) => (
              <FilterSelectOption key={module.module_code} value={module.module_code}>
                {module.customer_name || module.module_code}
              </FilterSelectOption>
            ))}
          </FilterSelect>

          <FilterSelect
            label={t('common:category')}
            hideLabel
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            minWidth={180}
          >
            {CATEGORY_FILTERS.map((filter) => (
              <FilterSelectOption key={filter.value || 'all'} value={filter.value}>
                {t(filter.labelKey)}
              </FilterSelectOption>
            ))}
          </FilterSelect>

          {activeTab === TAB_CAPABILITIES && (
            <>
              <FilterSelect
                label={t('features:columns.sourceType')}
                hideLabel
                value={sourceTypeFilter}
                onChange={(e) => setSourceTypeFilter(e.target.value)}
                minWidth={180}
              >
                {SOURCE_TYPE_FILTERS.map((filter) => (
                  <FilterSelectOption key={filter.value || 'all'} value={filter.value}>
                    {t(filter.labelKey)}
                  </FilterSelectOption>
                ))}
              </FilterSelect>

              <FilterSelect
                label={t('features:columns.guardPermission')}
                hideLabel
                value={guardPermissionFilter}
                onChange={(e) => setGuardPermissionFilter(e.target.value)}
                minWidth={200}
              >
                <FilterSelectOption value="">{t('features:filters.all')}</FilterSelectOption>
                {guardPermissionOptions.map((value) => (
                  <FilterSelectOption key={value} value={value}>
                    {value}
                  </FilterSelectOption>
                ))}
              </FilterSelect>

              <FilterSelect
                label={t('features:filters.globalRequired')}
                hideLabel
                value={globalRequiredFilter}
                onChange={(e) => setGlobalRequiredFilter(e.target.value)}
                minWidth={180}
              >
                <FilterSelectOption value="">{t('features:filters.all')}</FilterSelectOption>
                <FilterSelectOption value="true">{t('common:yes')}</FilterSelectOption>
                <FilterSelectOption value="false">{t('common:no')}</FilterSelectOption>
              </FilterSelect>
            </>
          )}
        </FilterBar>
      )}

      {loading ? (
        <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
          <TableToolbar
            mode="Default"
            title={t('features:technicalFeaturesTitle')}
            meta={(
              <StatusBadge
                status="list"
                category="generic"
                apiLabel={t('features:toolbar_count', { count: 0 })}
                size="Sm"
                tone="Neutral"
              />
            )}
          />
          <LoadingSkeleton type="Table" embedded rows={3} columns={Math.min(columns.length, 8)} />
        </SectionCard>
      ) : !error && groupedFeatures.length === 0 ? (
        <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
          <TableToolbar
            mode="Default"
            title={t('features:technicalFeaturesTitle')}
            meta={(
              <StatusBadge
                status="list"
                category="generic"
                apiLabel={t('features:toolbar_count', { count: 0 })}
                size="Sm"
                tone="Neutral"
              />
            )}
          />
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={[]}
            getRowKey={(row) => String(row.id || row.code || row.capability_code)}
            emptyMessage={t('features:features.no_features')}
            sx={tableSx}
          />
        </SectionCard>
      ) : !error ? (
        <Stack spacing={2.5}>
          {groupedFeatures.map((group) => (
            <SectionCard key={group.key} variant="Default" noPadding sx={{ width: '100%' }}>
              <TableToolbar
                mode="Default"
                title={
                  activeTab === TAB_LEGACY || groupedFeatures.length === 1
                    ? (activeTab === TAB_LEGACY ? group.label : t('features:technicalFeaturesTitle'))
                    : group.label
                }
                meta={(
                  <StatusBadge
                    status="list"
                    category="generic"
                    apiLabel={t('features:toolbar_count', { count: group.rows.length })}
                    size="Sm"
                    tone="Neutral"
                  />
                )}
              />
              <DataTable
                type="Default"
                embedded
                columns={columns}
                rows={group.rows}
                getRowKey={(row) => String(row.id || row.code || row.capability_code)}
                emptyMessage={t('features:features.no_features')}
                sx={tableSx}
              />
            </SectionCard>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
